import { Box, Center } from '@chakra-ui/layout'
import { CircularProgress } from '@chakra-ui/progress'
import { ColorModeProvider, extendTheme, ThemeProvider } from '@chakra-ui/react'
import * as _ from 'lodash'
import queryString from 'query-string'
import { useEffect, useState } from 'react'
import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import * as api from './api'
import {
  AuthenticationError,
  Footer,
  Header,
  MaintenancePage,
  NoMatch,
  ScrollToTop,
  type ChangeLanguageHandler,
} from './components'
import i18n from './i18n'
import { Main, StaticPage } from './pages'

const isMaintenance = false
const maintenanceReturnDate: string | undefined = undefined //'12:00 UTC on 05/20/2026'

const theme = extendTheme({
  styles: {
    global: () => ({
      a: {
        color: '#dc3545',
        _hover: {
          textDecoration: 'underline',
        },
      },
    }),
  },

  config: {
    useSystemColorMode: false,
    initialColorMode: 'light',
    cssVarPrefix: 'michelin',
  },
  colors: {
    michelinPrimary: '#BA0B2F',
    michelin: '#BA0B2F',
  },
})

const App = () => {
  const noUser = {} as api.APIUser
  const [ready, setReady] = useState(false)
  const [isValidToken, setIsValidToken] = useState(false)
  const [user, setUser] = useState(noUser)
  const [enableLanguageChange, setEnableLanguageChange] = useState(false)
  const [token, setToken] = useState('')
  const [footerPage, setFooterPage] = useState('')

  const checkValidToken = async (tokenValue: string) => {
    if (tokenValue) {
      localStorage.setItem('token', tokenValue)
      const user: api.APIUser | undefined = await api.checkToken(tokenValue)
      const validToken = !_.isUndefined(user) && !_.isEmpty(user)
      setIsValidToken(validToken)
      if (validToken) {
        setUser(user)
        const languageConfig = buildLanguageConfig(user)
        setEnableLanguageChange(languageConfig.changeEnabled)
        console.debug('[App.checkValidToken] newLanguage', languageConfig.language)
        i18n.changeLanguage(_.toLower(languageConfig.language.replace('-', '_')))
      }
      setReady(true)
    }
  }

  const changeLanguageHandler: ChangeLanguageHandler = async (
    user: api.APIUser,
    newLanguage: string
  ) => {
    setReady(false)
    await asyncChangeLanguage(user, newLanguage)
    setReady(true)
  }

  useEffect(() => {
    checkValidToken(token)
  }, [token])

  useEffect(() => {
    if (api.init()) {
      const result = readToken()

      if (_.get(result, 'footerPage')) {
        setFooterPage(_.get(result, 'footerPage', ''))
      }
      const tokenParam = _.get(result, 'token')
      console.debug('[App.useEffect] tokenParam', tokenParam)
      setToken(_.get(result, 'token', ''))

      if (!tokenParam) {
        setIsValidToken(false)
        setReady(true)
      }
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <ColorModeProvider
        value="light"
        options={{
          useSystemColorMode: false,
        }}>
        <Router
          future={{
            v7_relativeSplatPath: true,
            v7_startTransition: true,
          }}>
          <ScrollToTop />
          {ready ? (
            <>
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <Layout user={user} />
                    </>
                  }>
                  <Route
                    index
                    element={
                      isMaintenance ? (
                        <MaintenancePage returnDate={maintenanceReturnDate} />
                      ) : isValidToken ? (
                        <Main
                          user={user}
                          enableLanguageChange={enableLanguageChange}
                          changeLanguageHandler={changeLanguageHandler}
                        />
                      ) : (
                        <AuthenticationError />
                      )
                    }
                  />
                  {!isMaintenance && isValidToken ? (
                    <>
                      <Route
                        path="/terms-of-use"
                        element={<StaticPage user={user} pageName="terms-of-use-html-content" />}
                      />
                      <Route
                        path="/privacy-policy"
                        element={<StaticPage user={user} pageName="privacy-html-content" />}
                      />
                      <Route
                        path="/legal-notice"
                        element={<StaticPage user={user} pageName="legal-notice-html-content" />}
                      />
                      <Route
                        path="/cookie-policy"
                        element={<StaticPage user={user} pageName="cookie-policy-html-content" />}
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  <Route
                    path="*"
                    element={
                      <>
                        {footerPage ? (
                          <StaticPage user={user} footerPage={footerPage} />
                        ) : isMaintenance ? (
                          <MaintenancePage />
                        ) : isValidToken ? (
                          <NoMatch user={user} />
                        ) : (
                          <AuthenticationError />
                        )}
                      </>
                    }
                  />
                </Route>
              </Routes>
            </>
          ) : (
            <Center minHeight={['625px', '660px']}>
              <CircularProgress isIndeterminate={true} size={42} color="michelinPrimary" />
            </Center>
          )}
        </Router>
      </ColorModeProvider>
    </ThemeProvider>
  )
}

interface LayoutProps {
  user?: api.APIUser
}

const Layout = ({ user }: LayoutProps) => {
  return (
    <Box bg="#E5E5E5" w="100%">
      <Header />
      <Box justifyContent="center" minHeight={['625px', '660px']} bgColor="white" w="100%">
        <Outlet />
      </Box>
      <Footer user={user} />
    </Box>
  )
}

const asyncChangeLanguage: ChangeLanguageHandler = async (
  user: api.APIUser,
  newLanguage: string
) => {
  // change language in database for future use
  await api.changeLanguage(user.token, newLanguage, _.get(user, 'inspecId', ''))
  // change front language
  i18n.changeLanguage(_.toLower(newLanguage.replace('-', '_')))
}

const buildLanguageConfig = (user: api.APIUser): { language: string; changeEnabled: boolean } => {
  const result = {
    language: _.get(user, 'spokenLanguages[0]', 'en'),
    changeEnabled: false,
  }
  const country = _.get(user, 'country', '')
  switch (_.toLower(country)) {
    case 'canada':
      result.changeEnabled = true
      if (result.language.includes('fr')) {
        result.language = 'fr-QC'
      } else {
        result.language = 'en-QC'
      }
      break
    case 'hong kong':
      result.language = 'zh-HK'
      break
    case 'israel':
      result.language = 'en-IL'
      break
    case 'macao':
      result.language = 'zh-HK'
      break
    case 'malaysia':
      result.language = 'en-MY'
      break
    case 'morocco':
      result.language = 'fr-MA'
      break
    case 'mexico':
      result.language = 'es-MX'
      break
    case 'qatar':
      result.language = 'en-QA'
      break
    case 'united arab emirates':
      result.language = 'en-AE'
      break
    case 'singapore':
      result.language = 'en-SG'
      break
    case 'saudi arabia':
      result.language = 'en-SA'
      break
    default:
      break
  }
  return result
}

const readToken = (): { token?: string; footerPage?: string } => {
  const result: { token?: string; footerPage?: string } = {
    token: undefined,
    footerPage: undefined,
  }
  const params = queryString.parse(window.location.search)
  console.log('[App.useEffect] params', params)

  const path = _.get(window, 'location.pathname', '')
  if (/^\/[a-z]{2}-(cookie-policy||legal-notice||terms-of-use||privacy).html$/.test(path)) {
    result.footerPage = path.replace('/', '')
    i18n.changeLanguage(path.substring(1, 3))
  }

  result.token = readTokenParam(_.get(params, 'token'))
  if (_.isUndefined(result.token)) {
    const value = localStorage.getItem('token')
    result.token = _.isNull(value) ? undefined : value
  }
  return result
}

const readTokenParam = (tokenParams: string | null | (string | null)[]): string | undefined => {
  if (_.isNull(tokenParams)) {
    return undefined
  }
  if (_.isString(tokenParams)) {
    return tokenParams
  }
  if (_.isArray(tokenParams) && !_.isEmpty(tokenParams)) {
    if (_.isNull(tokenParams[0])) {
      return undefined
    }
    return tokenParams[0]
  }
  return undefined
}

export default App
